import { FirebaseError } from 'firebase/app';
import {
  ErrorBody,
  Responsible,
  ViolationReport
} from 'src/api/deinverband-api';
import {
  Locale,
  lookupTemplate,
  resolveLocale,
  substitute
} from 'src/libs/error-catalog';

const genericError = 'Es ist ein Fehler aufgetreten';

export class LocalizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LocalizedError';
  }
}

export class MissingSearchParameterError extends LocalizedError {
  constructor(public param: string) {
    super('Die angeforderte URL ist fehlerhaft');
    this.name = 'MissingSearchParameterError';
  }
}

export class UnsupportedApiError extends Error {
  constructor() {
    super('Unsupported API operation');
    this.name = 'UnsupportedApiError';
  }
}

export class MissingObjectError extends Error {
  constructor() {
    super(`Missing object for operation`);
    this.name = 'MissingObjectError';
  }
}

export class InvalidContextError extends Error {
  constructor(message: string) {
    super(`Invalid context: ${message}`);
    this.name = 'InvalidContextError';
  }
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// carries a list of per-field violations
const isViolationReport = (data: unknown): data is ViolationReport =>
  isObject(data) &&
  typeof data.key === 'string' &&
  Array.isArray(data.violations);

// carries a stable key plus a fallback template
const isErrorBody = (data: unknown): data is ErrorBody =>
  isObject(data) &&
  typeof data.key === 'string' &&
  typeof data.message === 'string';

/**
 * Render a backend ErrorBody into a localized, placeholder-substituted string
 *
 * The server never pre-renders: `message` is the raw EN template. We always
 * look up the localized template by `key` (falling back to the German catalog,
 * then to the EN template shipped in `message`) and substitute `messageArgs`.
 */
export const renderError = (
  body: ErrorBody,
  locale: Locale = resolveLocale()
): string =>
  substitute(
    templateFallback(body.key, locale, body.message),
    body.messageArgs
  );

// who is responsible for the constraint
export const errorResponsible = (error: unknown): Responsible | undefined =>
  isObject(error) && 'data' in error && isErrorBody(error.data)
    ? error.data.responsible
    : undefined;

const templateFallback = (
  key: string,
  locale: Locale,
  fallback = genericError
): string => {
  const template = lookupTemplate(key, locale);
  if (template) return template;

  // improve debugging without catalog
  console.warn(`[errors] Encountered error: ${key}`);
  return fallback;
};

/**
 * Bean-validation and JSON-decoding failures
 *
 * The per-field validator messages are the payload (key is for analytics only).
 * Fall back to the catalog message for the key when there are no field details.
 */
const violationText = (report: ViolationReport, locale: Locale): string => {
  const fields = report.violations
    .map(({ field, message }) => (field ? `${field}: ${message}` : message))
    .filter(Boolean);

  // return joined field messages if available
  if (!fields.length) return templateFallback(report.key, locale);

  return fields.join('\n');
};

export const errorText = (
  error?: unknown,
  locale: Locale = resolveLocale()
): string => {
  if (typeof error === 'string') return error;

  if (!isObject(error)) {
    console.warn('[errors] Non-object error:', error);
    return genericError;
  }

  // frontend internal error
  if (error instanceof LocalizedError) return error.message;

  // firebase error
  if (error instanceof FirebaseError) {
    if (error.code === 'auth/user-not-found') return 'Benutzer nicht gefunden';
    if (error.code === 'auth/wrong-password') return 'Passwort nicht korrekt';
    if (error.code === 'auth/weak-password') return 'Passwort zu unsicher';
    if (error.code === 'auth/invalid-action-code') return 'Code nicht gültig';
  }

  // backend error
  if ('status' in error) {
    const data = 'data' in error ? error.data : undefined;
    if (isViolationReport(data)) return violationText(data, locale);
    if (isErrorBody(data)) return renderError(data, locale);

    // last resort for a 403 without a structured body
    if (error.status === 403)
      return 'Sie haben nicht die erforderlichen Rechte';
  }

  console.warn('[errors] Unknown error:', error);
  return genericError;
};
